import React, { useCallback } from 'react';
import { gridSpacing } from '../../common/themes/constants';
import { Grid, Stack } from '@mui/material';
import { HookFormTextField, MyTypography, HookFormTextAreaAutoSize } from '../common/components';
import * as yup from 'yup';
import MobileNumberMask from '../common/components/mask/MobileNumberMask';
import HookFormDatePicker from '../common/components/hook-form/HookFormDatePicker';
import SSNMask from '../common/components/mask/SSNMask';
import moment from 'moment';
import AddressZipMask from '../common/components/mask/AddressZipMask';
import { isEmpty } from 'lodash';
import HookFormRadio from '../common/components/hook-form/HookFormRadio';
import HookFormCheckboxGroup from '../common/components/hook-form/HookFormCheckboxGroup';
import ReferralProviderOptions from '../widget/ReferralProviderOptions';
import AddressAutocomplete from '../common/components/address/AddressAutoComplete';
import ClinicDropdown from '../common/components/Clinics/ClinicDropdown';
import { Controller } from 'react-hook-form';
import ProviderDropdown from '../common/components/Providers/ProviderDropdown';

const today = moment().subtract(0, 'days');

export const initialValues = {
  referral: {
    clinic_receiver_id: -1,
    provider_id: -1,
    diagnosis: '',
    comment: '',
    referrer_provider_id: '',
  },
  patient: {
    firstname: '',
    lastname: '',
    phone: '',
    ssn: '',
    mrn: '',
    birthdate: '',
    full_address: '',
    insurance_provider: '',
    policy_number: '',
  },
};

export const createReferralValidationSchema = {
  diagnosis: yup.string().required('Diagnosis is required'),
  comment: yup.string().required('Comment is required'),
};

export const createPatientValidationSchema = {
  firstname: yup.string().required('First name is required'),
  lastname: yup.string().required('Last name is required'),
  ssn: yup
    .string()
    .test('length', 'SSN must have 9 digits', value => {
      const digits = value.replace(/\D/g, ''); // Remove non-digit characters
      return digits.length === 9;
    })
    .required('SSN is required')
    .typeError('SSN is required'),
  mrn: yup.string().required('MRN is required'),
  birthdate: yup
    .date('Birthdate is invalid.')
    .max(today, 'Birthdate must be today or a past date.')
    .required('Birthdate is required.')
    .typeError('Birthdate is invalid.'),
  phone: yup
    .string()
    .required('Phone number is required')
    .test('length', 'Phone number must have 10 digits', value => {
      const digits = value.replace(/\D/g, ''); // Remove non-digit characters
      return digits.length === 10;
    }),
  full_address: yup.string(),
  insurance_provider: yup.string().required('Insurance Provider is required'),
  policy_number: yup.string().required('Policy Number is required'),
};


export const renderField = (custom, prefix, control, errors, getLabel, getInputProps, handleRetrieve, watch) => {
  const getPlaceholder = (fieldName, label) => {
    if (fieldName === 'ssn') {
      return '###-##-####';
    } else {
      return 'Enter ' + label;
    }
  };

  const placeholder = getPlaceholder(custom.field, custom.label);
  const label = getLabel(custom);

  switch (custom.kind) {
    case 'label':
      return (
        <MyTypography variant="h5" component="h5" color="text.primary" sx={{ fontWeight: 'bold' }}>
          {label}
        </MyTypography>
      );
    case 'single_choice':
      return (
        <HookFormRadio
          id={custom.field}
          name={custom.field}
          choices={custom.choices || []}
          placeholder={placeholder}
          label={label}
          schema={custom.field}
          prefix={prefix}
          control={control}
          errors={errors}
          fullWidth
        />
      );
    case 'multiple_choice':
      return (
        <HookFormCheckboxGroup
          id={custom.field}
          name={custom.field}
          choices={custom.choices || []}
          placeholder={placeholder}
          label={label}
          schema={custom.field}
          prefix={prefix}
          control={control}
          errors={errors}
          fullWidth
        />
      );
    case 'text':
    case 'phone':
      return (
        <HookFormTextField
          id={custom.field}
          name={custom.field}
          placeholder={placeholder}
          label={label}
          schema={custom.field}
          prefix={prefix}
          control={control}
          helpText={custom.helpText}
          errors={errors}
          InputProps={getInputProps(custom.field)}
          fullWidth
        />
      );
    case 'date':
      return (
        <HookFormDatePicker
          id={custom.field}
          name={custom.field}
          placeholder={placeholder}
          label={label}
          schema={custom.field}
          disableFuture
          prefix={prefix}
          control={control}
          errors={errors}
          fullWidth
        />
      );
    case 'address':
      return (
        <AddressAutocomplete
          control={control}
          name={custom.field}
          errors={errors}
          prefix={prefix}
          placeholder={placeholder}
          handleRetrieve={handleRetrieve}
          label={label}
        />
      );
    case 'textarea':
      return (
        <HookFormTextAreaAutoSize
          id={custom.field}
          name={custom.field}
          placeholder={placeholder}
          label={label}
          schema={custom.field}
          prefix={prefix}
          control={control}
          errors={errors}
          style={{ width: '100%' }}
          minRows={3}
        />
      );

    case 'clinic':
      const clinic_id = watch("clinic_id");

      return (
        <Stack spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="clinic_id"
              control={control}
              render={({ field }) => (
                <ClinicDropdown
                  label={'Clinic'}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.clinic_receiver_id}
                  helperText={errors.clinic_receiver_id?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="provider_id"
              control={control}
              render={({ field }) => (
                <ProviderDropdown
                  clinic={clinic_id ? { id: clinic_id } : null}
                  label="Provider"
                  value={field.value}
                  forFax={true}
                  onChange={field.onChange}
                  error={!!errors.provider_id}
                  helperText={errors.provider_id?.message}
                  isReceiver={false}
                />
              )}
            />
          </Grid>
        </Stack>
      );
    default:
      return null;
  }
};

export default function PatientDemographicsForm({
  control,
  errors,
  prefix = null,
  isSameOrg = false,
  requiredCustom = [],
  watch,
  setValue,
  lastRequiredIndex = -1,
  providers = [],
  handleRetrieve = null,
}) {

  const getPrefix = useCallback((fieldName, pretext) => {
    return fieldName.includes('custom_') ||
      fieldName.includes('diagnosis') ||
      fieldName.includes('comment') ||
      fieldName.includes('referrer_provider_id')
      ? 'referral'
      : pretext;
  }, []);

  const getLabel = useCallback(
    custom => {
      return (
        custom.label +
        (custom.required ||
          (isSameOrg &&
            (custom.field === 'firstname' || custom.field === 'lastname' || custom.field === 'ssn'))
          ? ' *'
          : '')
      );
    },
    [isSameOrg],
  );

  const getWidth = useCallback(custom => {
    if (custom.kind === 'label') {
      return 12;
    }
    return !isEmpty(custom.size) ? custom.size : 6;
  }, []);

  const getInputProps = useCallback(fieldName => {
    if (fieldName === 'ssn') {
      return {
        inputComponent: SSNMask,
      };
    } else if (fieldName === 'phone') {
      return { inputComponent: MobileNumberMask };
    } else if (fieldName === 'address_zip') {
      return {
        inputComponent: AddressZipMask,
      };
    } else {
      return null;
    }
  }, []);

  return (
    <form autoComplete="off">
      <Grid container spacing={gridSpacing}>
        <Grid item sm={12} xs={12}>
          <h3 style={{ margin: '0' }}>Patient Demographics</h3>
        </Grid>
        {!isEmpty(providers) && (
          <Grid item sm={12} xs={12}>
            <ReferralProviderOptions
              prefix={'referral'}
              control={control}
              errors={errors}
              providers={providers}
              schema={'referrer_provider_id'}
              watch={watch}
              setValue={setValue}
            />
          </Grid>
        )}
        {!isEmpty(requiredCustom) &&
          requiredCustom.map((custom, index) => (
            <React.Fragment key={custom.field || index}>
              <Grid item sm={getWidth(custom)} xs={12} style={{ display: custom.hidden ? 'none' : 'block' }}>
                {renderField(custom, getPrefix(custom.field, prefix), control, errors, getLabel, getInputProps, handleRetrieve)}
              </Grid>
              {isSameOrg && index === lastRequiredIndex && (
                <Grid item sm={12} xs={12}>
                  <MyTypography variant="h5" component="h5" color="text.primary">
                    The following fields can be omitted since you are sending to a clinic within your own organization.
                  </MyTypography>
                </Grid>
              )}
            </React.Fragment>
          ))}
      </Grid>
    </form>
  );
}
